/* eslint-disable react/require-default-props */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable consistent-return */
import { Button, Popover } from '@airbus/components-react';
import { UnfoldLess, UnfoldMore } from '@airbus/icons/react';
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { DEFAULT_MULTI_LABEL } from '../../../utils/constants';
import './AdvanceComboBox.scss';
import AdvanceComboBoxContent from './AdvanceComboBoxContent';

// Option interface for dropdown items
interface Option {
  name: string;
  checked: boolean;
}
// Props for AdvanceComboBox component
type SearchPattern = 'contains' | 'startsWith';
// Props for AdvanceComboBox component
interface AdvanceComboBoxProps {
  loadOptions: () => void;
  filterOptions: Record<string, boolean>;
  placeholder?: string;
  showApply?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  listHeight?: number;
  rowHeight?: number;
  virtualizationThreshold?: number;
  loading: boolean;
  applyHandler?: (selected: string[]) => void;
  searchPattern?: SearchPattern;
  sortOptions?: boolean; // If true, sort options by ASCII order of name
}

/**
 * AdvanceComboBox
 * -----------------------------------------------------------------------------
 * A highly customizable and reusable combo box (dropdown) React component.
 *
 * Features:
 * - Asynchronous loading of options via a provided loadOptions function.
 * - Supports both single and multiple selection modes.
 * - Virtualized rendering for large option lists to improve performance.
 * - Search input for filtering options.
 * - Optional "Apply" and "Cancel" actions for multi-select scenarios.
 * - "Select All", "Deselect All", and "Clear" actions for convenience.
 * - Fully controlled via props for placeholder, disabled state, styling, and more.
 * - Integrates with @airbus/components-react for consistent UI.
 *
 * Usage:
 * Pass a loadOptions function that returns a Promise resolving to an object
 * with an array of options ({label, value}) and a hasMore flag.
 *
 * Example:
 * <AdvanceComboBox
 *   loadOptions={myAsyncLoader}
 *   multiple={true}
 *   placeholder="Choose items"
 * />
 *
 * This component is suitable for forms, filters, and anywhere a flexible
 * dropdown with advanced features is needed.
 */
const AdvanceComboBox: React.FC<AdvanceComboBoxProps> = ({
  loadOptions,
  filterOptions,
  placeholder = '',
  showApply = false,
  disabled = false,
  id = 'advance-combo-box',
  className = '',
  listHeight = 250,
  rowHeight = 40,
  virtualizationThreshold = 10,
  applyHandler = undefined,
  loading,
  searchPattern = 'contains',
  sortOptions = false,
}) => {
  // Reference to the trigger button for popover positioning
  const reference = useRef<HTMLButtonElement | null>(null);

  // State for loaded options, selected options, popover open state, search input, and loading indicator
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [comboBoxOptions, setComboBoxOptions] = useState<Option[]>(Object.entries(filterOptions).map(([name, checked]) => ({ name, checked })));
  const [checkedOptions, setCheckedOptions] = useState<Array<string>>([]);
  const [checkedOptionsCount, setCheckedOptionsCount] = useState<number>(0);
  const isFirstRender = useRef<boolean>(true);

  const filteredOptions = useMemo(() => {
    let options = [...comboBoxOptions];
    if (sortOptions) {
      const topOption = options.find((item) => item.name === DEFAULT_MULTI_LABEL); // returns the option where item.name is 'None / Multiple values'
      const restOptions = options.filter((item) => item.name !== DEFAULT_MULTI_LABEL); // returns all other options except 'None / Multiple values'
      const sortedRestOptions = [...restOptions].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: false, sensitivity: 'base' })); // sort rest of the options in lexicographical order
      options = topOption ? [topOption, ...sortedRestOptions] : sortedRestOptions;
    }
    if (search.length) {
      if (searchPattern === 'startsWith') {
        return options.filter((item: Option) => item.name.toLowerCase().startsWith(search.toLowerCase()));
      }
      // default to 'contains'
      return options.filter((item: Option) => item.name.toLowerCase().includes(search.toLowerCase()));
    }
    return options;
  }, [search, comboBoxOptions, searchPattern, sortOptions]);

  useEffect(() => {
    setComboBoxOptions(Object.entries(filterOptions).map(([name, checked]) => ({ name, checked })));
  }, [filterOptions]);

  useEffect(() => {
    setCheckedOptions(
      comboBoxOptions.filter((item: Option) => item.checked).map((item: Option) => item.name),
    );
    setCheckedOptionsCount(comboBoxOptions.reduce((count, item) => count + (item.checked ? 1 : 0), 0));
  }, [comboBoxOptions]);

  // Fetch options when dropdown opens or search changes
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (open) {
      loadOptions();
    } else if (applyHandler) {
      applyHandler(checkedOptions);
    }
    // eslint-disable-next-line
  }, [open]);

  /**
   * Returns the label to display on the trigger button based on selection.
   */
  const getButtonLabel = () => {
    // Always show placeholder if combobox is disabled
    if (disabled) return placeholder;
    if (checkedOptionsCount === 0) return placeholder;
    // Show All(...) only if all options are checked
    if (checkedOptionsCount === comboBoxOptions.length && comboBoxOptions.length > 0) {
      return `All(${comboBoxOptions.length})`;
    }
    if (checkedOptionsCount === 1) return checkedOptions[0];
    return `${checkedOptionsCount} Selected`;
  };

  /**
   * Handles selection/deselection of an option.
   */
  const handleOptionChange = (option: AcbObjectType) => {
    setCheckedOptions([...checkedOptions, option.name]);
    const updatedOptions = comboBoxOptions.map((item: Option) => {
      if (item.name === option.name) {
        return { ...item, checked: !option.checked };
      }
      return item;
    });
    setComboBoxOptions(updatedOptions);
  };

  /**
   * Handles select all/clear all logic for multi-select.
   */
  const handleSelectAll = (selectAll: boolean) => {
    const optionsToProcess: Array<string> = filteredOptions.map((item: Option) => item.name);
    const updatedOptions = comboBoxOptions.map((item: Option) => {
      if (optionsToProcess.includes(item.name)) {
        return { ...item, checked: selectAll };
      }
      return item;
    });
    setComboBoxOptions(updatedOptions);
  };

  /**
   * Clears all selections.
   */
  const handleClear = (clearAll: boolean) => {
    const optionsToProcess: Array<string> = filteredOptions.map((item: Option) => item.name);
    const updatedOptions = comboBoxOptions.map((item: Option) => {
      if (optionsToProcess.includes(item.name)) {
        return { ...item, checked: clearAll };
      }
      return item;
    });
    setComboBoxOptions(updatedOptions);
  };

  /**
   * Closes dropdown on outside click.
   */
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        reference.current
        && !reference.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <div id={id} className="advance-combo-box">
      {/* Trigger button for combo box */}
      <div className="advance-combo-button">
        <Button
          id={`${id}-ACB-button`}
          data-testid="advance-combo-button"
          ref={reference}
          disabled={disabled}
          onClick={() => {
            setOpen((o) => !o);
            setSearch('');
          }}
          className="advance-combo-trigger"
          placeholder={undefined}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        >
          {getButtonLabel()}
          <span className="arrow">
            {open ? <UnfoldLess /> : <UnfoldMore />}
          </span>
        </Button>
      </div>
      {/* Popover containing the dropdown content */}
      <Popover
        open={open}
        placement="bottom"
        referenceNode={reference.current}
        className={className}
        placeholder={undefined}
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
      >
        <AdvanceComboBoxContent
          showApply={showApply}
          loading={loading}
          search={search}
          setSearch={setSearch}
          handleOptionChange={handleOptionChange}
          handleSelectAll={handleSelectAll}
          handleClear={handleClear}
          setOpen={setOpen}
          listHeight={listHeight}
          rowHeight={rowHeight}
          virtualizationThreshold={virtualizationThreshold}
          filterOptions={filteredOptions}
          searchPattern={searchPattern}
        />
      </Popover>
    </div>
  );
};

export default AdvanceComboBox;
